import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useLiveUpdates } from '../hooks/useLiveUpdates';
import Badge from './ui/Badge';

type FeedTone = 'new' | 'warning' | 'critical';

interface FeedItem {
  id: string;
  reportNumber?: string;
  title: string;
  priority: string;
  status: string;
  tone: FeedTone;
  reason: string;
  minutesRemaining?: number;
  minutesOverdue?: number;
  hoursOverdue?: number;
  minutesUnassigned?: number;
  createdAt?: string;
}

interface FeedResponse {
  counts: { new: number; approaching: number; escalated: number; total: number };
  items: FeedItem[];
}

const QUERY_KEY = ['notifications-feed'];
// Baseline poll — the websocket push (new_report / sla_breach) triggers an
// immediate refetch on top of this, so this interval is just the fallback
// for missed/dropped socket frames, not the primary delivery path.
const POLL_MS = 30000;

const TONE_RING: Record<FeedTone, string> = {
  new: 'ring-primary-500',
  warning: 'ring-accent-500',
  critical: 'ring-red-600',
};

const reasonLabel = (item: FeedItem) => {
  switch (item.reason) {
    case 'new_report':
      return 'Just submitted';
    case 'sla_approaching':
      return `${item.minutesRemaining}m left on SLA`;
    case 'sla_breached':
      return 'SLA breached';
    case 'sla_overdue':
      return `${item.minutesOverdue}m overdue`;
    case 'escalation_critical':
    case 'escalation_high':
      return `${item.hoursOverdue}h overdue, unresolved`;
    case 'unassigned_stuck':
      return `${item.minutesUnassigned}m with nobody assigned`;
    default:
      return item.status;
  }
};

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery<FeedResponse>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await apiService.get<FeedResponse>('/notifications/feed');
      return response.data as unknown as FeedResponse;
    },
    refetchInterval: POLL_MS,
  });

  // A new_report or sla_breach push means the feed is now stale — refetch
  // right away instead of waiting up to 30s for the next poll.
  useLiveUpdates((message) => {
    if (message.type === 'new_report' || message.type === 'sla_breach' || message.type === 'alert') {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    }
  });

  React.useEffect(() => {
    const onClickAway = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const counts = data?.counts ?? { new: 0, approaching: 0, escalated: 0, total: 0 };
  const items = data?.items ?? [];

  // Highest-severity tone present drives the bell's own colour/animation —
  // one escalated fault outranks any number of merely-new ones.
  const dominantTone: FeedTone | null = useMemo(() => {
    if (counts.escalated > 0) return 'critical';
    if (counts.approaching > 0) return 'warning';
    if (counts.new > 0) return 'new';
    return null;
  }, [counts]);

  // 'new' means unassigned (the feed's new-report bucket is status='submitted'
  // — a report that's already been auto-assigned moves to 'acknowledged' and
  // drops out of it), so it gets the same red treatment as an escalated fault:
  // nobody is on it yet, which is exactly what should interrupt someone.
  const bellButtonClasses = useMemo(() => {
    const base = 'relative rounded-sm p-2 transition-colors';
    if (dominantTone === 'critical' || dominantTone === 'new') return `${base} text-red-600 hover:bg-red-50 dark:hover:bg-red-950`;
    if (dominantTone === 'warning') return `${base} text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-950`;
    return `${base} text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200`;
  }, [dominantTone]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={`Notifications${counts.total ? `, ${counts.total} unread` : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={bellButtonClasses}
      >
        <BellIcon className={`h-5 w-5 ${dominantTone === 'critical' || dominantTone === 'new' ? 'animate-pulse' : ''}`} />
        {counts.total > 0 && (
          <span
            className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-900 ${
              dominantTone === 'critical' || dominantTone === 'new' ? 'animate-flash-critical' : dominantTone === 'warning' ? 'bg-accent-600' : 'bg-primary-600'
            }`}
          >
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-96 max-w-[90vw] rounded-lg bg-white shadow-pop ring-1 ring-gray-200 animate-slide-up dark:bg-gray-800 dark:ring-gray-700"
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Fault Notifications</h3>
            <div className="flex gap-1.5">
              {counts.escalated > 0 && <Badge tone="danger">{counts.escalated} escalated</Badge>}
              {counts.approaching > 0 && <Badge tone="warning">{counts.approaching} approaching</Badge>}
              {counts.new > 0 && <Badge tone="danger">{counts.new} unassigned</Badge>}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">Nothing needs attention right now.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {items.map((item) => (
                  <li key={`${item.reason}-${item.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(`/reports/${item.id}`);
                      }}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                        item.tone === 'critical' ? `ring-1 ring-inset ${TONE_RING.critical}` : ''
                      }`}
                    >
                      <span
                        className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                          item.tone === 'critical' || item.tone === 'new' ? 'animate-flash-critical' : 'bg-accent-500'
                        }`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.title}
                          </span>
                          <Badge value={item.priority} />
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          {item.reportNumber ? `${item.reportNumber} — ` : ''}
                          {reasonLabel(item)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
